import { Type } from "@themes/pollinator/ui/Type";
import { MarketingSection } from "../../components/Section";

export type Props = {
  onNavigationAnchorClick: (
    e: React.MouseEvent<HTMLAnchorElement>,
    targetId: string
  ) => void;
};

export function MarketingVision({ onNavigationAnchorClick }: Props) {
  return (
    <MarketingSection
      title="We are building artist-led technology to weave relationships of mutual support."
      id="vision"
    >
      <Type variant="h4" className="max-w-3xl">
        Why this? Why now?
      </Type>
      <Type variant="body1" className="max-w-3xl">
        We believe that interdisciplinary practitioners across art, design,
        technology, and social impact have the desire and capacity to create
        opportunities for one another. Pollinator exists to build
        communities of solidarity across sectors, to make us all more
        resilient amidst crisis.
      </Type>
      <ul className="list-disc list-inside flex flex-col gap-6">
        <Type variant="body2" as="li" className="max-w-3xl">
          Relationships drive access to opportunity. While cohort programs
          like residencies and art schools elevate careers temporarily,
          aligned peer groups sustain us. Great people want to meet and
          support other great people, and by joining forces, we can go
          farther together.
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          It <span className="italic">is</span> possible to resist the
          endless scroll, and to instead create digital spaces of generative
          conflict and belonging. While the nature of content engagement has
          changed, our desire to form bonds in real time has not. The power
          of human-to-human intelligence is great, and our minds come alive
          in group settings.
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          Lastly, we believe that you, our participants, will guide and
          support us as we continue to build Pollinator. We are here to
          create infrastructure that is made by, for, and with artists, in
          service of mutual support, and we value your contributions to this
          project.
        </Type>
      </ul>
      <Type variant="h4" className="max-w-3xl">
        Your presence—being caring, curious, and open—is more transformative
        than your expertise.
      </Type>
      <Type variant="body1" className="max-w-3xl">
        If Pollinator expanded its offerings to include additional features
        and platforms in 2027, what would you want?
      </Type>
      <ul className="list-disc list-inside flex flex-col gap-2">
        <Type variant="body2" as="li" className="max-w-3xl">
          a program where we are matched and meet one time only,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          an in-person option for sessions,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a certificate in peer-coaching and jobs,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          access to expert coaches for 1:1 work,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a mentorship program and economy,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a LinkedIn-type platform,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a chat/Discord/Listserv-type community,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a resources-roundup newsletter,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a grant/funding/mutual aid program,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a directory of artist-run spaces, collectives, and more,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a place for grantmakers to find and fund participants,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          a social media platform by and for us,
        </Type>
        <Type variant="body2" as="li" className="max-w-3xl">
          something else.
        </Type>
      </ul>
      <Type variant="body1" className="max-w-3xl">
        Submit your ideas and requests to{" "}
        <a
          href="mailto:caroline@pollinator.coop"
          className="underline hover:text-brand-700"
        >
          caroline@pollinator.coop
        </a>
      </Type>
      <Type variant="body1" className="max-w-3xl italic">
        If you love the container we are creating, please consider making a{" "}
        <a
          href="/#donate"
          className="underline hover:text-brand-700"
          onClick={(e: React.MouseEvent<HTMLAnchorElement>) =>
            onNavigationAnchorClick(e, "donate")
          }
        >
          tax-deductible donation to support our work.
        </a>
      </Type>
    </MarketingSection>
  );
}

export default MarketingVision;
